import { labelForMetric } from '@chiroup/core/constants/metricCategories';
import { XMarkIcon } from '@heroicons/react/24/outline';
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import useEventLogEvents from '../../hooks/useEventLogEvents';
import { metricKeyToEventType } from './metricToEventType';

type Props = {
  open: boolean;
  onClose: () => void;
  clinicId: number;
  metricKey: string | null;
  startDate: string;
  endDate: string;
  userId?: string;
};

const formatTimestamp = (ms: number): { date: string; time: string } => {
  const d = new Date(ms);

  return {
    date: d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
    time: d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }),
  };
};

const MetricDrawer: React.FC<Props> = ({
  open,
  onClose,
  clinicId,
  metricKey,
  startDate,
  endDate,
  userId,
}) => {
  // Lock body scroll while drawer is open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);

    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const eventType = metricKey ? metricKeyToEventType(metricKey) : undefined;

  const { data, isFetching, fetchNextPage, isFetchingNextPage, hasNextPage } = useEventLogEvents(
    clinicId,
    { eventType, startDate, endDate, userId },
    open && !!eventType
  );

  if (!open || !metricKey) return null;

  const events = data?.pages?.flatMap(p => p.data || []) || [];
  const total = data?.pages?.[0]?.count ?? 0;

  return createPortal(
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/30 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div className="ml-auto relative h-full w-full max-w-md bg-white shadow-2xl flex flex-col">
        <div className="border-b border-gray-200 px-5 py-4 flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold">
              {labelForMetric(metricKey)}
            </div>
            <div className="text-sm text-gray-700 mt-0.5">
              {total.toLocaleString()} {total === 1 ? 'event' : 'events'}
              {userId && <span className="text-gray-400"> · filtered to user</span>}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 rounded p-1 -m-1"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {isFetching && events.length === 0 ? (
            <div className="px-5 py-8 text-center text-sm text-gray-500">Loading…</div>
          ) : events.length === 0 ? (
            <div className="px-5 py-12 text-center text-sm text-gray-500">No events found</div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {events.map(ev => {
                const ts = formatTimestamp(ev.occurredAt);
                const meta = ev.metadata ? JSON.stringify(ev.metadata) : null;

                return (
                  <li key={ev.id} className="px-5 py-3 hover:bg-gray-50">
                    <div className="flex items-baseline justify-between gap-3">
                      <div className="text-sm font-medium text-gray-900">
                        {ev.userName || ev.userId || 'System'}
                      </div>
                      <div className="text-xs text-gray-400 whitespace-nowrap">
                        {ts.date} · {ts.time}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2">
                      {ev.locationId != null && <span>Location {ev.locationId}</span>}
                      {meta && (
                        <span className="font-mono text-[10px] text-gray-400 truncate">{meta}</span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {hasNextPage && (
            <div className="px-5 py-4 text-center">
              <button
                type="button"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium disabled:opacity-50"
              >
                {isFetchingNextPage ? 'Loading…' : 'Load more events'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default MetricDrawer;
